import { useState, useEffect, useRef, useMemo } from "react";
import "./styles/animations.css";
import { supabase } from "./lib/supabase";
import { useCloudSync } from "./hooks/useCloudSync";
import { useBetRemoval } from "./hooks/useBetRemoval";
import { useGameData } from "./hooks/useGameData";
import { useNotGradedGames } from "./hooks/useNotGradedGames";
import { mergeNotGradedGames } from "./lib/not-graded-games";
import { useWireRealtime } from "./hooks/useWireRealtime";
import { useSlipTrackData } from "./hooks/useSlipTrackData";
import { isNative } from "./lib/nativeAuth";
import { Capacitor } from "@capacitor/core";
import { useAuth } from "./hooks/useAuth";
import { useNetworkStatus } from "./hooks/useNetworkStatus";
import { useAppUpdateCheck } from "./hooks/useAppUpdateCheck";
import { useRosters } from "./hooks/useRosters";
import { useOdds } from "./hooks/useOdds";
import { useBetaStatus } from "./hooks/useBetaStatus";
import { useStatsNavigation } from "./hooks/useStatsNavigation";
import { useScreenTracking } from "./hooks/useScreenTracking";
import { resolveLegToStatsNav } from "./lib/leg-stats-link";
import BottomSheet from "./components/BottomSheet";
import AddSlipSheet from "./features/slips/AddSlipSheet";
import BookiesPage from "./features/bookies/BookiesPage";
import BugReporter from "./features/bugs/BugReporter";
import UpdateBanner from "./components/ui/UpdateBanner";
import OfflineStatusBanner from "./components/ui/OfflineStatusBanner";
import TabBar from "./components/ui/TabBar";
import ImportMethodSheet from "./components/ImportMethodSheet";
import ErrorBoundary from "./components/ErrorBoundary";
import { TabCrashCard } from "./components/ui/CrashFallback";

import HomePage from "./pages/HomePage";
import MySlipsPage from "./pages/MySlipsPage";
import StatsPage from "./pages/StatsPage";
import ResultsTab from "./pages/ResultsTab";
import { PAGE } from "./constants/pages";
import { JT } from "./components/ui/jumbotronTokens";
import AccountSheet from "./features/account/AccountSheet";
import OnboardingGate from "./features/onboarding/OnboardingGate";


// ─── LIVE STAT TRACKING ───────────────────────────────────────────────────────
// NHL boxscore returns names as "F. Lastname" — match against full "Firstname Lastname"
// NHL abbreviation → full name mapping for Odds API matching
// ─── HAPTICS ──────────────────────────────────────────────────────────────────
const haptic = {
  light:   () => {},
  medium:  () => {},
  heavy:   () => {},
  success: () => {},
  error:   () => {},
};


// ─── PAGES ────────────────────────────────────────────────────────────────────
const PAGES = ["Home","My Slips","Stats","Results","The Bookies"];
// 44pt header control (iOS HIG / PRODUCT.md). Visual chrome stays small; the hit area does not.
const HEADER_BTN = {minWidth:44,minHeight:44,display:"flex",alignItems:"center",justifyContent:"center",background:"none",border:`1px solid ${JT.border.mid}`,borderRadius:10,padding:0,color:JT.text.secondary,cursor:"pointer",fontFamily:"inherit"};

// Render-crash containment for the active tab (#1116): header, tab bar and sheets stay usable;
// a tab switch (resetKey) or Try again clears it.
const tabCrashFallback = ({ reset }) => <TabCrashCard onRetry={reset}/>;

export default function SlipTrack() {
  const { user, showAuth, authResolved, signInWithGoogle, signInWithApple, reauthorizeWithApple, canReauthorizeWithApple, openAuth, closeAuth } = useAuth();
  const [page,setPage]=useState(PAGE.HOME);
  // v1c Stats deep-link: SlipTicket taps flow through here into the locked 4-prop
  // nav contract; App stays a conductor and never interprets the payload (spec §F).
  const { statsNav, requestPlayerNav, requestSearchNav, clearStatsNav } = useStatsNavigation();
  // Monotonic token so the latest USER action wins, not the last async response:
  // a slow resolve for leg A that lands after leg B (or after a manual tab tap) must
  // not overwrite the newer navigation (GPT pre-audit P1#2).
  const statsNavSeq = useRef(0);
  const openPlayerStats = async (descriptor) => {
    const my = ++statsNavSeq.current;
    const nav = await resolveLegToStatsNav(descriptor);
    if (my !== statsNavSeq.current) return; // a newer tap / manual nav superseded this one
    if (nav.kind === "player") requestPlayerNav(nav); else requestSearchNav(nav.query);
    setPage(PAGE.RESEARCH);
  };
  // Manual navigation to the Stats tab starts fresh: clear any stale deep-link so a
  // later tab tap shows the search view, not the last deep-linked player (v1b behavior).
  // Bumping the token also cancels any in-flight resolve so the manual action wins.
  const goToPage = (i) => { statsNavSeq.current += 1; if (i === PAGE.RESEARCH) clearStatsNav(); setPage(i); };
  const [bets,setBets]=useState([]);
  const userRef = useRef(user);
  userRef.current = user;
  const prevUserIdRef = useRef(null);
  // #1214 PR B (spec §16.5): the guarded setter goes ONLY to useCloudSync; the realtime hooks
  // (useSlipTrackData) keep the raw setBets so their channel deps never change.
  const { guardedSetBets, removeBet, addBet } = useBetRemoval({ user, setBets });
  const { cloudSynced, syncFailed, syncLoading, syncError, retrySync } = useCloudSync({
    user,
    setBets: guardedSetBets,
    userRef,
    prevUserIdRef,
  });

  // Import entry state machine (spec §1a/§4.5): null=closed | "method"=ImportMethodSheet | "screenshot"=AddSlipSheet.
  const [importSurface,setImportSurface]=useState(null);
  const [showBugReport,setShowBugReport]=useState(false);
  const [accountOpen,setAccountOpen]=useState(false);
  const pullStartY = useRef(0);
  const { updateAvailable } = useAppUpdateCheck();
  const { isOnline } = useNetworkStatus();
  const betaGate = useBetaStatus({ user });

  const { games, dataSource, lastUpdated, loadGames } = useGameData();
  const [wire, setWire] = useState([]);
  // enabled gates on auth: `news` is authenticated-read, so seeding before a session
  // attaches returns [] silently and never re-seeds. Reseeds once when user arrives.
  useWireRealtime({ setWire, enabled: Boolean(user) });
  const { rosters, rostersLoaded } = useRosters();
  const { liveStats } = useSlipTrackData({ user, bets, setBets });
  const { oddsData } = useOdds({ games });
  // #1655 B3b A1: the old FINAL rows a NOT GRADED bet needs once its game leaves the 3-day window.
  // Only the status consumers get the merged list; odds, the import sheet and the Home rail stay
  // on the window. No extra rows -> `games` itself (identity kept).
  const notGradedGames = useNotGradedGames({ bets, games });
  const statusGames = useMemo(() => mergeNotGradedGames(games, notGradedGames), [games, notGradedGames]);
  // PostHog screen_viewed (spec §4 step 6): gated off until the initial session check
  // finishes (#1306) and while the auth screen shows.
  // The onboarding gate (#1416) reports when the app is visible; it starts PostHog first, so the
  // landing screen_viewed is not lost into an uninitialized SDK.
  const [gateOpen, setGateOpen] = useState(false);
  useScreenTracking(page, authResolved && gateOpen);

  // Onboarding gate (#1416, spec §4): age, Terms and sign-in screens, or the app. It owns the old
  // showAuth sign-in gate and PostHog start/pause (D9).
  return (
    <OnboardingGate auth={{ user, showAuth, authResolved, closeAuth, signInWithGoogle, signInWithApple }} onAppVisible={setGateOpen}>
    <div style={{height:"100dvh",boxSizing:"border-box",background:"#050d1a",fontFamily:"'SF Pro Display','DM Sans',system-ui,sans-serif",color:"#e2e8f0",maxWidth:480,margin:"0 auto",display:"flex",flexDirection:"column",paddingTop:"env(safe-area-inset-top)",paddingLeft:"env(safe-area-inset-left)",paddingRight:"env(safe-area-inset-right)"}}>

      <div style={{padding:"48px 20px 0",background:"linear-gradient(180deg,#060e1c,transparent)",position:"sticky",top:0,zIndex:10,backdropFilter:"blur(24px)"}}>
        <div style={{marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          {/* UI shell + token floor (2026-09-20): 11px Label at a readable color; the version /
              build pills moved to AccountSheet (and BugReporter already carries them); every
              header control is a 44pt hit area. */}
          <div data-testid="header-date" style={{...JT.type.label,color:JT.text.tertiary}}>{new Date().toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"}).replace(",","")}</div>
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <button onClick={()=>setShowBugReport(true)} aria-label="Report a bug" style={{...HEADER_BTN,fontSize:16}}>🐛</button>
            <button onClick={()=>{haptic.light();goToPage(PAGE.BOOKIES);}} aria-label="The Bookies" style={{...HEADER_BTN,gap:4,padding:"0 10px"}}>
              <span aria-hidden="true" style={{fontSize:16}}>🏟</span><span style={{...JT.type.label,color:JT.text.secondary}}>Bookies</span>
            </button>
            {user && supabase ? (
              <button onClick={()=>setAccountOpen(true)} title="Account" style={{...HEADER_BTN,border:"none"}}>
                <span style={{width:26,height:26,borderRadius:"50%",border:`1px solid ${JT.border.mid}`,background:JT.bg.card,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {user.user_metadata?.avatar_url
                    ? <img src={user.user_metadata.avatar_url} alt="avatar" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                    : <span style={{...JT.type.label,color:JT.text.secondary}}>{(user.email||"?")[0].toUpperCase()}</span>
                  }
                </span>
              </button>
            ) : (
              <button onClick={()=>{ openAuth(); }} style={{...HEADER_BTN,padding:"0 12px",color:JT.brand.accent,fontWeight:700,fontSize:13}}>
                Sign in
              </button>
            )}
          </div>
        </div>
      </div>

      {!isNative() && <UpdateBanner version={updateAvailable}/>}
      <OfflineStatusBanner isOnline={isOnline}/>
      <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch"}}
        onTouchStart={e=>{ if(page===PAGE.HOME||page===PAGE.BETS) pullStartY.current=e.touches[0].clientY; }}
        onTouchEnd={e=>{
          if((page===PAGE.HOME||page===PAGE.BETS)&&pullStartY.current>0&&e.changedTouches[0].clientY-pullStartY.current>80){
            haptic.light();
            loadGames();
          }
          pullStartY.current=0;
        }}>
        <ErrorBoundary name="tab" resetKey={page} context={{ page: PAGES[page] }} fallback={tabCrashFallback}>
        {page===PAGE.HOME&&<HomePage games={games} statusGames={statusGames} bets={bets} oddsData={oddsData} dataSource={dataSource} lastUpdated={lastUpdated} wire={wire} onViewBets={()=>{haptic.light();goToPage(PAGE.BETS);}} onImport={()=>{haptic.light();setImportSurface("method");}} syncLoading={syncLoading} syncError={syncError} onRetrySync={retrySync}/>}
        {page===PAGE.BETS&&<MySlipsPage bets={bets} games={statusGames} liveStats={liveStats} oddsData={oddsData} haptic={haptic} onAdd={()=>setImportSurface("screenshot")} user={user} onSignIn={signInWithGoogle} onSyncComplete={retrySync} onOpenPlayerStats={openPlayerStats} refetchBetaStatus={betaGate.refetch} syncLoading={syncLoading} syncError={syncError} onRetrySync={retrySync} removeBet={removeBet}/>}
        {page===PAGE.RESEARCH&&<StatsPage {...statsNav}/>}
        {page===PAGE.HISTORY&&<ResultsTab bets={bets} games={statusGames} refetchBetaStatus={betaGate.refetch} syncLoading={syncLoading} syncError={syncError} onRetrySync={retrySync} user={user} removeBet={removeBet} haptic={haptic}/>}
        {page===PAGE.BOOKIES&&<BookiesPage user={user} games={statusGames}/>}
        </ErrorBoundary>
      </div>

      <TabBar
        activePage={page}
        onNavigate={(i)=>{haptic.light();goToPage(i);}}
        onImport={()=>{haptic.light();setImportSurface("method");}}
      />

      {/* Two-path import entry (spec §1a). Always mounted so useSlipSync survives an
          active sync while the sheet is visually closed (open toggles visibility). */}
      <ImportMethodSheet
        open={importSurface==="method"}
        onClose={()=>setImportSurface(null)}
        dataSource={dataSource}
        user={user}
        onSyncComplete={retrySync}
        onChooseScreenshot={()=>setImportSurface("screenshot")}
        onSignIn={signInWithGoogle}
      />

      {games.length>0&&(
        <AddSlipSheet open={importSurface==="screenshot"} games={games} onAdd={async (bet)=>addBet(bet)} onClose={()=>setImportSurface(null)} rosters={rosters} rostersLoaded={rostersLoaded} oddsData={oddsData} user={user} onSignIn={signInWithGoogle} isOnline={isOnline} isReady={cloudSynced} syncFailed={syncFailed} retrySync={retrySync} betaGate={betaGate}/>
      )}

      {/* Bug Reporter Sheet */}
      <BottomSheet open={showBugReport} onClose={()=>setShowBugReport(false)}>
        <BugReporter currentPage={PAGES[page]} onClose={()=>setShowBugReport(false)} user={user} isOnline={isOnline} cloudSynced={cloudSynced}/>
      </BottomSheet>
      {/* Account Sheet (#1079) */}
      <AccountSheet open={accountOpen} onClose={()=>setAccountOpen(false)} user={user} isOnline={isOnline} reauthorizeWithApple={reauthorizeWithApple} canReauthorizeWithApple={canReauthorizeWithApple}/>
    </div>
    </OnboardingGate>
  );
}

